import React, { useState } from 'react';
import { LockIcon, CloseIcon, ShieldCheckIcon, CheckCircleIcon } from './Icons';
import { sounds } from '../utils/audio';

export default function ParentGateModal({ isOpen, onClose }) {
  const [unlocked, setUnlocked] = useState(false);
  const [num1] = useState(7);
  const [num2] = useState(4);
  const [answerInput, setAnswerInput] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Parent settings state
  const [dailyLimit, setDailyLimit] = useState('20');
  const [musicPref, setMusicPref] = useState(true);
  const [sfxPref, setSfxPref] = useState(true);

  if (!isOpen) return null;

  const handleVerify = (e) => {
    e.preventDefault();
    if (parseInt(answerInput, 10) === num1 * num2) {
      sounds.playSound('chest');
      setUnlocked(true);
      setErrorMsg('');
    } else {
      sounds.playSound('tap');
      setErrorMsg('Incorrect answer. Please calculate carefully to verify you are a grown-up.');
    }
  };

  const handleClose = () => {
    sounds.playSound('tap');
    setUnlocked(false);
    setAnswerInput('');
    setErrorMsg('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-pop">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border-4 border-kiki-purple space-y-6">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b pb-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-kiki-lavender rounded-xl text-kiki-purple">
              <LockIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-heading font-black text-xl text-slate-900">
                {unlocked ? 'Parent & Educator Dashboard' : 'Parent Verification Gate'}
              </h3>
              <p className="text-xs text-slate-500 font-semibold">
                {unlocked ? 'Adjust settings & view progress summaries' : 'Grown-ups only: solve the math problem'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        {!unlocked ? (
          /* Gated Verification Form */
          <form onSubmit={handleVerify} className="space-y-4 py-2">
            <div className="p-5 bg-purple-50/70 rounded-2xl border-2 border-purple-200 text-center space-y-2">
              <span className="text-xs font-heading font-bold text-kiki-purple uppercase tracking-wider">
                Adult Verification Question
              </span>
              <div className="text-3xl font-heading font-black text-slate-800">
                {num1} &times; {num2} = ?
              </div>
              <p className="text-xs text-slate-500">
                Type the multiplication result below to proceed.
              </p>
            </div>

            <div className="space-y-2">
              <input
                type="number"
                placeholder="Type answer here..."
                value={answerInput}
                onChange={(e) => setAnswerInput(e.target.value)}
                autoFocus
                className="w-full text-center py-3.5 px-4 rounded-2xl border-2 border-slate-300 focus:border-kiki-purple focus:outline-none font-heading font-bold text-xl text-slate-800"
              />
              {errorMsg && (
                <p className="text-xs text-rose-600 font-semibold text-center">{errorMsg}</p>
              )}
            </div>

            <button
              type="submit"
              className="btn-bouncy w-full py-3.5 rounded-2xl bg-kiki-purple hover:bg-purple-700 text-white font-heading font-bold text-base shadow-md"
            >
              Unlock Parent Zone
            </button>
          </form>
        ) : (
          /* Unlocked Parent Dashboard */
          <div className="space-y-5 py-2">
            
            {/* Screen Time Limit Setting */}
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
              <span className="text-xs font-heading font-bold text-slate-700 block">
                Daily Recommended Session Limit
              </span>
              <div className="grid grid-cols-3 gap-2">
                {['15 min', '20 min', '30 min'].map((time) => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => { sounds.playSound('tap'); setDailyLimit(time); }}
                    className={`py-2 px-3 rounded-xl font-heading text-xs font-bold border ${
                      dailyLimit === time
                        ? 'bg-kiki-purple text-white border-kiki-purple shadow-sm'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>

            {/* Audio Preferences */}
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-heading font-bold text-slate-800 block">Background Music</span>
                  <span className="text-[11px] text-slate-500">Calm acoustic exploration theme</span>
                </div>
                <input
                  type="checkbox"
                  checked={musicPref}
                  onChange={(e) => setMusicPref(e.target.checked)}
                  className="w-5 h-5 accent-kiki-purple rounded cursor-pointer"
                />
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                <div>
                  <span className="text-xs font-heading font-bold text-slate-800 block">Sound Effects</span>
                  <span className="text-[11px] text-slate-500">Bubble pops and star sparkles</span>
                </div>
                <input
                  type="checkbox"
                  checked={sfxPref}
                  onChange={(e) => setSfxPref(e.target.checked)}
                  className="w-5 h-5 accent-kiki-purple rounded cursor-pointer"
                />
              </div>
            </div>

            {/* Offline Progress Security Note */}
            <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl text-xs text-emerald-800 font-semibold border border-emerald-200">
              <ShieldCheckIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <span>All progress is stored strictly offline. No telemetry leaves your home device.</span>
            </div>

            <button
              onClick={handleClose}
              className="btn-bouncy w-full py-3 rounded-2xl bg-slate-900 text-white font-heading font-bold text-sm shadow-md"
            >
              Done &amp; Save
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
